import { useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ProjectStatusBadge } from "@/mockups/ui";
import { ROLE_COLORS } from "@/pages/rrhh/Equipo";
import { openPayslip } from "@/pages/rrhh/Nominas";
import { ArrowLeft, Clock, KeyRound, IdCard, Wallet, CalendarClock, FolderKanban, Upload, FileText, Trash2, Check, Sparkles, Loader2, CheckCircle2 } from "lucide-react";
import { fmtHM } from "@/lib/utils";

const euro = (n) => (n == null ? "—" : Number(n).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }));
const euro0 = (n) => (n == null ? "—" : Number(n).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));
const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("");
const fmt = (d) => (d ? new Date(d).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "2-digit" }) : "—");
const VAC_STATUS = { pending: { label: "Pendiente", cls: "bg-amber-100 text-amber-800" }, approved: { label: "Aprobada", cls: "bg-emerald-100 text-emerald-800" }, rejected: { label: "Rechazada", cls: "bg-red-100 text-red-700" } };

function AccessTab({ detail, onSaved }) {
  const u = detail.user;
  const [form, setForm] = useState({ role: u.role, cost_profile: u.cost_profile || "Técnico", job_title: u.job_title || "", hourly_cost: u.hourly_cost ?? "", is_active: u.is_active });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/team/members/${u.user_id}/access`, {
        role: form.role, cost_profile: form.cost_profile, job_title: form.job_title,
        hourly_cost: form.hourly_cost === "" ? 0 : Number(form.hourly_cost),
        is_active: form.is_active,
      });
      toast.success("Acceso y coste actualizados");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); } finally { setSaving(false); }
  };
  return (
    <Card className="border-border shadow-none p-6 space-y-6" data-testid="tab-content-acceso">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl">
        <div className="space-y-1.5">
          <Label className="text-xs">Rol de acceso</Label>
          <Select value={form.role} onValueChange={(v) => set("role", v)}>
            <SelectTrigger data-testid="det-role"><SelectValue /></SelectTrigger>
            <SelectContent>{detail.roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Puesto</Label>
          <Input value={form.job_title} onChange={(e) => set("job_title", e.target.value)} placeholder="Ej: Dir. Producción" data-testid="det-job" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Perfil de coste</Label>
          <Select value={form.cost_profile} onValueChange={(v) => set("cost_profile", v)}>
            <SelectTrigger data-testid="det-cost-profile"><SelectValue /></SelectTrigger>
            <SelectContent>{detail.cost_profiles.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Coste / hora (€)</Label>
          <Input type="number" step="0.01" value={form.hourly_cost} onChange={(e) => set("hourly_cost", e.target.value)}
            placeholder={u.effective_hourly_cost != null ? `${u.effective_hourly_cost} (por perfil)` : "según perfil"} data-testid="det-hourly-cost" />
          <p className="text-[11px] text-muted-foreground">Vacío = usa la tarifa del perfil de coste ({u.effective_hourly_cost != null ? euro(u.effective_hourly_cost) : "—"}/h).</p>
        </div>
      </div>
      <div className="flex items-center justify-between max-w-2xl border-t border-border pt-4">
        <div>
          <div className="text-sm font-medium">Usuario activo</div>
          <div className="text-xs text-muted-foreground">Un usuario inactivo no puede iniciar sesión ni se le imputan horas nuevas.</div>
        </div>
        <Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} data-testid="det-active" />
      </div>
      <div className="flex justify-end max-w-2xl">
        <Button onClick={save} disabled={saving} data-testid="det-access-save">{saving ? "Guardando…" : "Guardar acceso y coste"}</Button>
      </div>
    </Card>
  );
}

const LABOR_FIELDS = [
  { k: "nif", label: "NIF / DNI" }, { k: "employee_number", label: "Nº de empleado" },
  { k: "position", label: "Puesto (RRHH)" }, { k: "department", label: "Departamento" },
  { k: "contract_type", label: "Tipo de contrato" }, { k: "workday", label: "Jornada" },
];

function FichaTab({ detail, onSaved }) {
  const p = detail.profile;
  const [form, setForm] = useState({
    nif: p.nif || "", employee_number: p.employee_number || "", position: p.position || "",
    department: p.department || "", contract_type: p.contract_type || "", workday: p.workday || "",
    start_date: p.start_date || "", gross_annual: p.gross_annual ?? 0, num_payments: p.num_payments ?? 14,
    vacation_days_year: p.vacation_days_year ?? 23,
    phone: p.phone || "", address: p.address || "", iban: p.iban || "", emergency_contact: p.emergency_contact || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/hr/profiles/${detail.user.user_id}`, {
        ...form, gross_annual: Number(form.gross_annual) || 0,
        num_payments: Number(form.num_payments) || 14, vacation_days_year: Number(form.vacation_days_year) || 0,
      });
      toast.success("Ficha actualizada"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); } finally { setSaving(false); }
  };
  return (
    <Card className="border-border shadow-none p-6 space-y-6" data-testid="tab-content-ficha">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Datos laborales</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {LABOR_FIELDS.map((f) => (
            <div key={f.k} className="space-y-1.5"><Label className="text-xs">{f.label}</Label><Input value={form[f.k]} onChange={(e) => set(f.k, e.target.value)} data-testid={`profile-${f.k}-input`} /></div>
          ))}
          <div className="space-y-1.5"><Label className="text-xs">Fecha de alta</Label><Input type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} data-testid="profile-start_date-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Días de vacaciones / año</Label><Input type="number" value={form.vacation_days_year} onChange={(e) => set("vacation_days_year", e.target.value)} data-testid="profile-vacation-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Salario bruto anual (€)</Label><Input type="number" value={form.gross_annual} onChange={(e) => set("gross_annual", e.target.value)} data-testid="profile-gross-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Nº de pagas</Label><Input type="number" value={form.num_payments} onChange={(e) => set("num_payments", e.target.value)} /></div>
        </div>
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Contacto</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label className="text-xs">Teléfono</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
          <div className="space-y-1.5"><Label className="text-xs">IBAN</Label><Input value={form.iban} onChange={(e) => set("iban", e.target.value)} /></div>
          <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs">Dirección</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
          <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs">Contacto de emergencia</Label><Input value={form.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} /></div>
        </div>
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="profile-save-btn">{saving ? "Guardando…" : "Guardar ficha"}</Button>
      </div>
    </Card>
  );
}

function VacacionesTab({ detail }) {
  const s = detail.vacation_summary || {};
  const reqs = detail.vacation_requests || [];
  return (
    <div className="space-y-4" data-testid="tab-content-vacaciones">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Asignados", value: s.assigned }, { label: "Disfrutados", value: s.taken },
          { label: "Próximos/aprobados", value: s.pending }, { label: "Disponibles", value: s.available },
        ].map((c, i) => (
          <Card key={i} className="border-border shadow-none p-4"><div className="font-heading font-bold text-2xl tabular-nums">{c.value ?? "—"}</div><div className="text-xs text-muted-foreground mt-1">{c.label}</div></Card>
        ))}
      </div>
      <Card className="border-border shadow-none overflow-hidden">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead className="pl-5">Fechas</TableHead><TableHead className="text-center">Días</TableHead><TableHead>Tipo</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
          <TableBody>
            {reqs.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground text-sm">Sin solicitudes.</TableCell></TableRow>
            ) : reqs.map((r) => {
              const st = VAC_STATUS[r.status] || VAC_STATUS.pending;
              return (
                <TableRow key={r.id}>
                  <TableCell className="pl-5 text-sm tabular-nums">{r.from_date === r.to_date ? fmt(r.from_date) : `${fmt(r.from_date)} → ${fmt(r.to_date)}`}</TableCell>
                  <TableCell className="text-center text-sm tabular-nums">{r.days}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.type}</TableCell>
                  <TableCell><Badge className={`${st.cls} hover:${st.cls} font-normal`}>{st.label}</Badge></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function NominasTab({ detail, onSaved }) {
  const uid = detail.user.user_id;
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const payslips = detail.payslips || [];
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("user_id", uid); fd.append("file", file);
      const res = await api.post("/hr/payslips/upload", fd);
      toast.success(res.data.extracted ? "Nómina procesada por IA" : "Nómina subida (revisa los importes)");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error al subir"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const validar = async (id) => { try { await api.post(`/hr/payslips/${id}/validar`); toast.success("Validada · el empleado ya la ve"); onSaved(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const del = async (id) => { if (!window.confirm("¿Eliminar esta nómina?")) return; try { await api.delete(`/hr/payslips/${id}`); toast.success("Eliminada"); onSaved(); } catch (e) { toast.error("Error"); } };
  return (
    <Card className="border-border shadow-none p-5 space-y-4" data-testid="tab-content-nominas">
      <div className="rounded-lg border border-dashed border-border p-4 flex items-center justify-between gap-3 bg-muted/30">
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Sparkles className="h-4 w-4 text-gold" /> {uploading ? "Procesando con IA…" : "Subir nómina en PDF (bruto/IRPF/SS por IA)"}</div>
        <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={onUpload} data-testid="det-payslip-input" />
        <Button size="sm" className="gap-1.5" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="det-payslip-upload">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Subir PDF
        </Button>
      </div>
      <div className="overflow-x-auto">
        <Table className="min-w-[640px]">
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead className="pl-1">Periodo</TableHead><TableHead className="text-right">Bruto</TableHead><TableHead className="text-right">SS empresa</TableHead><TableHead className="text-right">IRPF</TableHead><TableHead className="text-right">Neto</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
          <TableBody>
            {payslips.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground text-sm">Sin nóminas todavía.</TableCell></TableRow>
            ) : payslips.map((p) => (
              <TableRow key={p.id} data-testid={`det-payslip-${p.id}`}>
                <TableCell className="pl-1 text-sm">{p.month_label || p.period || "—"}</TableCell>
                <TableCell className="text-right tabular-nums text-sm">{euro(p.gross)}</TableCell>
                <TableCell className="text-right tabular-nums text-sm">{euro(p.coste_empresa)}</TableCell>
                <TableCell className="text-right tabular-nums text-sm">{euro(p.irpf)}</TableCell>
                <TableCell className="text-right tabular-nums text-sm">{euro(p.net)}</TableCell>
                <TableCell>{p.share_status === "compartida" ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 gap-1"><CheckCircle2 className="h-3 w-3" />Compartida</Badge> : <Badge variant="secondary">Pendiente</Badge>}</TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    {p.share_status !== "compartida" && <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" title="Validar" onClick={() => validar(p.id)} data-testid={`det-payslip-validate-${p.id}`}><Check className="h-4 w-4" /></Button>}
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Ver PDF" onClick={() => openPayslip(p.id)}><FileText className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" title="Eliminar" onClick={() => del(p.id)} data-testid={`det-payslip-del-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function ProyectosTab({ detail }) {
  const projects = detail.projects || [];
  return (
    <Card className="border-border shadow-none overflow-hidden" data-testid="tab-content-proyectos">
      <Table>
        <TableHeader><TableRow className="hover:bg-transparent"><TableHead className="pl-5">Proyecto</TableHead><TableHead>Referencia</TableHead><TableHead>Estado</TableHead><TableHead className="text-right pr-5">Horas imputadas</TableHead></TableRow></TableHeader>
        <TableBody>
          {projects.length === 0 ? (
            <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground text-sm">Sin proyectos asignados.</TableCell></TableRow>
          ) : projects.map((p) => (
            <TableRow key={p.id}>
              <TableCell className="pl-5 text-sm font-medium"><Link to={`/proyecto/${p.id}`} className="hover:text-primary hover:underline">{p.name}</Link></TableCell>
              <TableCell className="text-xs text-muted-foreground">{p.reference || "—"}</TableCell>
              <TableCell><ProjectStatusBadge status={p.status} /></TableCell>
              <TableCell className="text-right pr-5 text-sm tabular-nums">{p.hours ? fmtHM(p.hours) : "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function HorasTab({ detail }) {
  const h = detail.hours || { total: 0, by_project: [] };
  return (
    <div className="space-y-4" data-testid="tab-content-horas">
      <Card className="border-border shadow-none p-4"><div className="font-heading font-bold text-2xl tabular-nums">{fmtHM(h.total)}</div><div className="text-xs text-muted-foreground mt-1">Total de horas imputadas</div></Card>
      <Card className="border-border shadow-none overflow-hidden">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead className="pl-5">Proyecto</TableHead><TableHead className="text-right pr-5">Horas</TableHead></TableRow></TableHeader>
          <TableBody>
            {(h.by_project || []).length === 0 ? (
              <TableRow><TableCell colSpan={2} className="text-center py-8 text-muted-foreground text-sm">Sin horas registradas.</TableCell></TableRow>
            ) : h.by_project.map((r) => (
              <TableRow key={r.project_id}>
                <TableCell className="pl-5 text-sm">{r.project_id ? <Link to={`/proyecto/${r.project_id}`} className="hover:text-primary hover:underline">{r.project_name}</Link> : r.project_name}</TableCell>
                <TableCell className="text-right pr-5 text-sm tabular-nums">{fmtHM(r.hours)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

export default function EquipoDetalle() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { data, loading, refetch } = useFetch(`/team/members/${uid}`);

  if (loading || !data) return <div className="py-24 flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const { user, access } = data;
  const defaultTab = access.econ ? "acceso" : "ficha";

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="equipo-detalle-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate("/rrhh/equipo")} data-testid="det-back"><ArrowLeft className="h-4 w-4" /></Button>
        <Avatar className="h-14 w-14"><AvatarImage src={user.picture} alt={user.name} /><AvatarFallback className="bg-muted">{initials(user.name)}</AvatarFallback></Avatar>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-heading font-bold text-xl truncate">{user.name}</h1>
            <Badge variant="outline" className={`border-transparent font-medium ${ROLE_COLORS[user.role]}`}>{user.role}</Badge>
            {user.pending_login && <span className="inline-flex items-center gap-1 text-[11px] text-gold"><Clock className="h-3 w-3" /> Pendiente de acceso</span>}
            {!user.is_active && <Badge variant="secondary" className="font-normal">Inactivo</Badge>}
          </div>
          <div className="text-sm text-muted-foreground">{user.email}{user.job_title ? ` · ${user.job_title}` : ""}</div>
        </div>
      </div>

      <Tabs defaultValue={defaultTab}>
        <TabsList className="flex-wrap h-auto" data-testid="detalle-tabs">
          {access.econ && <TabsTrigger value="acceso" data-testid="dtab-acceso"><KeyRound className="h-3.5 w-3.5 mr-1.5" /> Acceso y coste</TabsTrigger>}
          {access.rrhh && <TabsTrigger value="ficha" data-testid="dtab-ficha"><IdCard className="h-3.5 w-3.5 mr-1.5" /> Ficha</TabsTrigger>}
          {access.rrhh && <TabsTrigger value="vacaciones" data-testid="dtab-vacaciones"><CalendarClock className="h-3.5 w-3.5 mr-1.5" /> Vacaciones</TabsTrigger>}
          {access.rrhh && <TabsTrigger value="nominas" data-testid="dtab-nominas"><Wallet className="h-3.5 w-3.5 mr-1.5" /> Nóminas</TabsTrigger>}
          {access.econ && <TabsTrigger value="proyectos" data-testid="dtab-proyectos"><FolderKanban className="h-3.5 w-3.5 mr-1.5" /> Proyectos</TabsTrigger>}
          {access.econ && <TabsTrigger value="horas" data-testid="dtab-horas"><Clock className="h-3.5 w-3.5 mr-1.5" /> Horas</TabsTrigger>}
        </TabsList>

        {access.econ && <TabsContent value="acceso" className="mt-4"><AccessTab detail={data} onSaved={refetch} /></TabsContent>}
        {access.rrhh && <TabsContent value="ficha" className="mt-4"><FichaTab detail={data} onSaved={refetch} /></TabsContent>}
        {access.rrhh && <TabsContent value="vacaciones" className="mt-4"><VacacionesTab detail={data} /></TabsContent>}
        {access.rrhh && <TabsContent value="nominas" className="mt-4"><NominasTab detail={data} onSaved={refetch} /></TabsContent>}
        {access.econ && <TabsContent value="proyectos" className="mt-4"><ProyectosTab detail={data} /></TabsContent>}
        {access.econ && <TabsContent value="horas" className="mt-4"><HorasTab detail={data} /></TabsContent>}
      </Tabs>
    </div>
  );
}
